import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const easeSoft = [0.16, 1, 0.3, 1];

/* Full-bleed film layer with subtle parallax + slow entrance scale */
function FilmLayer({ film, poster, targetRef, parallax = true }) {
  const { scrollYProgress } = useScroll({ target: targetRef, offset: ["start end", "end start"], layoutEffect: false });
  const y = useTransform(scrollYProgress, [0, 1], parallax ? ["-8%", "8%"] : ["0%", "0%"]);
  return (
    <motion.div style={{ y }} className="absolute inset-0 will-change-transform">
      <motion.video
        initial={{ opacity: 0, scale: 1.08 }} animate={{ opacity: 1, scale: 1 }}
        transition={{ opacity: { duration: 2.8, ease: "easeOut" }, scale: { duration: 20, ease: "easeOut" } }}
        className="absolute inset-0 w-full h-[116%] object-cover"
        autoPlay muted loop playsInline preload="auto" poster={poster}
        onCanPlay={(e) => { const p = e.target.play(); if (p && p.catch) p.catch(() => {}); }}
      >
        <source src={film} type="video/mp4" />
      </motion.video>
    </motion.div>
  );
}

/* Full-bleed still-image layer — parallax + slow cinematic scale.
   Mirrors FilmLayer so a real film can replace the media later with no layout change. */
function ImageLayer({ image, targetRef, parallax = true }) {
  const { scrollYProgress } = useScroll({ target: targetRef, offset: ["start end", "end start"], layoutEffect: false });
  const y = useTransform(scrollYProgress, [0, 1], parallax ? ["-8%", "8%"] : ["0%", "0%"]);
  return (
    <motion.div style={{ y }} className="absolute inset-0 will-change-transform">
      <motion.img
        src={image} alt=""
        initial={{ opacity: 0, scale: 1.12 }} animate={{ opacity: 1, scale: 1 }}
        transition={{ opacity: { duration: 2.8, ease: "easeOut" }, scale: { duration: 24, ease: "easeOut" } }}
        className="absolute inset-0 w-full h-[116%] object-cover"
      />
    </motion.div>
  );
}

const rise = (delay = 0, y = 20) => ({
  initial: { opacity: 0, y }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.4 },
  transition: { duration: 1.8, delay, ease: easeSoft },
});

/* Opening hero film — typography is the hero */
export function CinematicHero({ film, poster, eyebrow, headline, sub, ctaLabel, ctaTo }) {
  const ref = useRef(null);
  return (
    <section ref={ref} data-testid="cinematic-hero" className="relative h-[100svh] w-full overflow-hidden bg-black">
      <FilmLayer film={film} poster={poster} targetRef={ref} />
      <div className="absolute inset-0 [background:radial-gradient(ellipse_at_center,rgba(0,0,0,0.15)_20%,rgba(0,0,0,0.55)_100%)]" />
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
        <motion.span {...rise(0.9)} className="font-sans text-[10px] uppercase tracking-[0.5em] text-white/70 mb-9">{eyebrow}</motion.span>
        <motion.h1 {...rise(1.6, 24)} className="font-serif font-light text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-[-0.01em] max-w-3xl">
          {headline}
        </motion.h1>
        {sub && (
          <motion.p {...rise(2.4, 16)} data-testid="hero-supporting-line" className="mt-8 font-serif italic text-white/65 text-base sm:text-lg font-light leading-relaxed max-w-xl">
            {sub}
          </motion.p>
        )}
        {ctaLabel && (
          <motion.div {...rise(3.1)} className="mt-11">
            <Link to={ctaTo} data-testid="hero-begin-cta" className="inline-flex items-center gap-2 border border-white/50 text-white px-9 py-4 text-[11px] uppercase tracking-[0.25em] hover:bg-white hover:text-black transition-all duration-500">
              {ctaLabel}
            </Link>
          </motion.div>
        )}
      </div>
      <motion.div {...rise(3.6)} className="absolute bottom-9 left-1/2 -translate-x-1/2">
        <motion.span animate={{ opacity: [0.25, 0.9, 0.25] }} transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }} className="block w-px h-10 bg-white/40" />
      </motion.div>
    </section>
  );
}

/* Full-screen cinematic chapter interstitial */
export function CinematicChapter({ film, poster, image, chapter, title, subtitle, intention, align = "center", overlay = "bg-black/40", testid, height = "h-[92svh]" }) {
  const ref = useRef(null);
  const isLeft = align === "left";
  return (
    <section ref={ref} data-testid={testid} className={`relative ${height} w-full overflow-hidden bg-black`}>
      {film ? <FilmLayer film={film} poster={poster} targetRef={ref} /> : image ? <ImageLayer image={image} targetRef={ref} /> : null}
      <div className={`absolute inset-0 ${overlay}`} />
      <div className={`relative z-10 h-full flex flex-col justify-center px-6 sm:px-12 lg:px-20 ${isLeft ? "items-start text-left" : "items-center text-center"}`}>
        {chapter && (
          <motion.span {...rise(0.2)} className="font-sans text-[10px] uppercase tracking-[0.5em] text-white/70 mb-7">{chapter}</motion.span>
        )}
        <motion.h2 {...rise(0.6, 22)} className={`font-serif font-light text-white text-3xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-[-0.01em] ${isLeft ? "max-w-2xl" : "max-w-3xl"}`}>
          {title}
        </motion.h2>
        {subtitle && (
          <motion.p {...rise(0.9)} data-testid={testid ? `${testid}-subtitle` : undefined} className={`mt-6 font-serif text-base sm:text-lg lg:text-xl font-light text-white/90 leading-relaxed ${isLeft ? "max-w-2xl" : "max-w-2xl"}`} style={{ textShadow: "0 1px 3px rgba(0,0,0,0.55)" }}>
            {subtitle}
          </motion.p>
        )}
        {intention && (
          <motion.p {...rise(1.2)} className="mt-8 font-sans text-[10px] sm:text-[11px] uppercase tracking-[0.4em] text-white/60">
            {intention}
          </motion.p>
        )}
      </div>
    </section>
  );
}

/* Cinematic editorial montage — a slow "visual essay" of authentic photography
   with gentle Ken Burns motion and elegant crossfades. Feels like a luxury
   documentary rather than a slideshow. A real film can replace this later
   using the same structure (see CinematicChapter's film prop). */
export function CinematicMontage({ slides = [], captions = [], chapter, title, intention, testid = "cinematic-montage", interval = 6500, endCtaLabel, endCtaTo, endCtaSecondaryLabel, endCtaSecondaryTo }) {
  const [i, setI] = useState(0);
  const reduced = prefersReducedMotion();
  const lastIndex = slides.length - 1;

  // Preload the collection so crossfades never flash.
  useEffect(() => {
    slides.forEach((s) => { const img = new Image(); img.src = s; });
  }, [slides]);

  // Advance through the essay continuously — a slow cinematic loop.
  // The CTA overlay still surfaces on the final frame each cycle.
  useEffect(() => {
    if (reduced || slides.length <= 1) return;
    const id = setInterval(() => {
      if (document.hidden) return;
      setI((p) => (p + 1) % slides.length);
    }, interval);
    return () => clearInterval(id);
  }, [slides.length, interval, reduced]);

  const caption = captions.length ? captions[i % captions.length] : null;
  const atEnd = i === lastIndex;

  return (
    <section data-testid={testid} className="relative h-[92svh] w-full overflow-hidden bg-black">
      <AnimatePresence>
        <motion.div
          key={i}
          className="absolute inset-0 will-change-[opacity]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 2.2, ease: "easeInOut" }}
        >
          <motion.img
            src={slides[i]}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ scale: reduced ? 1 : 1.04 }}
            animate={{ scale: reduced ? 1 : 1.18 }}
            transition={{ duration: reduced ? 0 : interval / 1000 + 4, ease: "linear" }}
          />
        </motion.div>
      </AnimatePresence>

      <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/20 to-black/65" />

      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 sm:px-12">
        {chapter && <motion.span {...rise(0.2)} className="font-sans text-[10px] uppercase tracking-[0.5em] text-white/70 mb-7">{chapter}</motion.span>}
        {title && <motion.h2 {...rise(0.6, 22)} className="font-serif font-light text-white text-3xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-[-0.01em] max-w-3xl">{title}</motion.h2>}
        {intention && <motion.p {...rise(1.2)} className="mt-8 font-sans text-[10px] sm:text-[11px] uppercase tracking-[0.4em] text-white/60 max-w-xl">{intention}</motion.p>}
        {endCtaLabel && atEnd && (
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.4, ease: "easeInOut", delay: 0.4 }}
            className="mt-12 flex flex-col sm:flex-row items-center gap-6"
          >
            <Link to={endCtaTo} data-testid={`${testid}-cta-primary`} className="inline-flex items-center gap-2 border border-white/60 text-white px-9 py-4 text-[11px] uppercase tracking-[0.25em] hover:bg-white hover:text-black transition-all duration-500">
              {endCtaLabel}
            </Link>
            {endCtaSecondaryLabel && (
              <Link to={endCtaSecondaryTo} data-testid={`${testid}-cta-secondary`} className="text-white/70 text-[11px] uppercase tracking-[0.25em] border-b border-white/30 pb-1 hover:text-white hover:border-white transition-all duration-500">
                {endCtaSecondaryLabel}
              </Link>
            )}
          </motion.div>
        )}
      </div>

      {/* Slide caption + progress — quiet, editorial */}
      <div className="absolute bottom-9 left-0 right-0 z-10 flex flex-col items-center gap-4">
        {caption && (
          <AnimatePresence mode="wait">
            <motion.span
              key={caption}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 0.75, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 1.1, ease: "easeInOut" }}
              className="font-sans text-[10px] uppercase tracking-[0.45em] text-white/75"
            >
              {caption}
            </motion.span>
          </AnimatePresence>
        )}
        <div className="flex items-center gap-2">
          {slides.map((_, d) => (
            <button
              key={d}
              onClick={() => setI(d)}
              aria-label={`View ${d + 1}`}
              data-testid={`${testid}-dot-${d}`}
              className={`h-1 rounded-full transition-all duration-500 ${d === i ? "w-6 bg-[var(--accent-bronze)]" : "w-1.5 bg-white/35 hover:bg-white/60"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/* Progressive reveal wrapper — fades content up as it enters the viewport */
export function Reveal({ children, delay = 0, y = 28, className = "", amount = 0.25 }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount }}
      transition={{ duration: 1.1, delay, ease: easeSoft }}
    >
      {children}
    </motion.div>
  );
}
